import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { vermieterApi, type VermieterCreateData } from '../api/vermieter';
import SortTh from '../components/SortTh';
import Spinner from '../components/Spinner';
import EmptyTableRow from '../components/EmptyTableRow';
import Pagination from '../components/Pagination';
import { useSessionFilter } from '../hooks/useSessionFilter';
import { useCan } from '../hooks/useCan';
import { useColumnConfig } from '../hooks/useConfigVisibility';
import { restoreOne } from '../api/softDelete';

export default function VermieterList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useSessionFilter<string>('vermieter_searchText', '');
  const [sortBy, setSortBy] = useSessionFilter<string>('vermieter_sortBy', 'name');
  const [sortDir, setSortDir] = useSessionFilter<'asc' | 'desc'>('vermieter_sortDir', 'asc');
  const [showHidden, setShowHidden] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState<VermieterCreateData>({ name: '', typ: '', email: '', telefon: '' });
  const [createError, setCreateError] = useState('');
  const canRestoreHidden = useCan('restore_hidden');
  const canRestoreOwn = useCan('restore_own');
  const cols = useColumnConfig('vermieter');
  const queryClient = useQueryClient();
  const perPage = 50;

  const queryArgs = { page, per_page: perPage, search_text: searchText || undefined, sort_by: sortBy, sort_dir: sortDir, include_hidden: showHidden };
  const { data, isLoading } = useQuery({
    queryKey: ['vermieter', queryArgs],
    queryFn: () => vermieterApi.getVermieter(queryArgs),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: number) => restoreOne('vermieter', id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vermieter'] }),
  });

  const createMutation = useMutation({
    mutationFn: (body: VermieterCreateData) => vermieterApi.createVermieter(body),
    onSuccess: (vermieter) => {
      queryClient.invalidateQueries({ queryKey: ['vermieter'] });
      navigate(`/vermieter/${vermieter.id}`);
    },
    onError: () => {
      setCreateError('Fehler beim Erstellen des Vermieters.');
    },
  });

  const handleCreate = () => {
    if (!form.name.trim()) {
      setCreateError('Name ist erforderlich.');
      return;
    }
    setCreateError('');
    createMutation.mutate({
      name: form.name.trim(),
      typ: form.typ?.trim() || undefined,
      email: form.email?.trim() || undefined,
      telefon: form.telefon?.trim() || undefined,
    });
  };

  const inputClass = "w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Search + New button */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-3">
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Suche nach Name, E-Mail, Notizen..."
              className="flex-1 border rounded-lg px-3 py-2 text-sm"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            />
            <button
              onClick={() => { setShowCreateForm(true); setCreateError(''); }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              + Neuer Vermieter
            </button>
          </div>
          {(canRestoreHidden || canRestoreOwn) && (
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => { setShowHidden(e.target.checked); setPage(1); }}
              />
              Ausgeblendete anzeigen
            </label>
          )}
        </div>

        {/* Create form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <h3 className="text-base font-medium text-gray-800 mb-4">Neuen Vermieter anlegen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="z.B. Max Mustermann"
                  className={inputClass}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Typ</label>
                <select
                  value={form.typ ?? ''}
                  onChange={(e) => setForm({ ...form, typ: e.target.value })}
                  className={inputClass}
                >
                  <option value="">-- kein Typ --</option>
                  <option value="privatperson">Privatperson</option>
                  <option value="gewerblich">Gewerblich</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">E-Mail</label>
                <input
                  type="email"
                  value={form.email ?? ''}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="z.B. name@example.com"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Telefon</label>
                <input
                  value={form.telefon ?? ''}
                  onChange={(e) => setForm({ ...form, telefon: e.target.value })}
                  placeholder="z.B. +49 30 1234567"
                  className={inputClass}
                />
              </div>
            </div>
            {createError && (
              <p className="text-sm text-red-600 mt-2">{createError}</p>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {createMutation.isPending ? 'Wird angelegt...' : 'Anlegen'}
              </button>
              <button
                onClick={() => { setShowCreateForm(false); setForm({ name: '', typ: '', email: '', telefon: '' }); setCreateError(''); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortTh field="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Name</SortTh>
                    {cols.typ !== false && <SortTh field="typ" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Typ</SortTh>}
                    {cols.email !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>}
                    {cols.telefon !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Telefon</th>}
                    {cols.inserate !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inserate</th>}
                    {cols.verfahren !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verfahren</th>}
                    <SortTh field="created_at" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Erstellt</SortTh>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.items?.map((v) => (
                    <tr
                      key={v.id}
                      className={`hover:bg-gray-50 cursor-pointer ${v.deleted_at ? 'bg-slate-100 opacity-60' : ''}`}
                      onClick={() => navigate(`/vermieter/${v.id}`)}
                    >
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">{v.name}</td>
                      {cols.typ !== false && (
                        <td className="px-3 py-3 text-sm">
                          {v.typ ? (
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              v.typ === 'gewerblich' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {v.typ}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      )}
                      {cols.email !== false && <td className="px-3 py-3 text-sm text-gray-500">{v.email || '-'}</td>}
                      {cols.telefon !== false && <td className="px-3 py-3 text-sm text-gray-500">{v.telefon || '-'}</td>}
                      {cols.inserate !== false && <td className="px-3 py-3 text-sm text-gray-500">{v.inserate_count}</td>}
                      {cols.verfahren !== false && (
                        <td className="px-3 py-3 text-sm">
                          {v.verfahren_count > 0 ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                              {v.verfahren_count}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {v.deleted_at ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); restoreMutation.mutate(v.id); }}
                            className="text-green-700 hover:text-green-900 text-sm font-medium"
                          >
                            Wiederherstellen
                          </button>
                        ) : (
                          new Date(v.created_at).toLocaleDateString('de-DE')
                        )}
                      </td>
                    </tr>
                  ))}
                  {(!data?.items || data.items.length === 0) && (
                    <EmptyTableRow colSpan={7} message="Keine Vermieter gefunden." />
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={data?.total ?? 0}
              entityLabel="Vermieter"
              onPageChange={setPage}
            />
          </>
        )}
      </main>
    </div>
  );
}
